(() => {
  'use strict';
  if (customElements.get('oa-search-filters')) return;
  const Search = customElements.get('oa-search');
  const oaScopeSearch = Search?.scopeSearch;
  if (!oaScopeSearch) return;
  const sectionSelector = '[data-oa-instant-section]';
  const responses = new Map();
  let sequence = 0, request, timer;
  const forms = root => [...root.querySelectorAll('oa-search-filters')];
  const cancel = () => { clearTimeout(timer); request?.abort(); return ++sequence; };
  const status = (root, message, busy = false, failed = false) => {
    root.setAttribute('aria-busy', String(busy));
    root.classList.toggle('oa-filter-loading', busy);
    forms(root).forEach(el => {
      if (el.status) el.status.textContent = message;
      el.classList.toggle('oa-refine--error', failed);
      const button = el.querySelector('[data-view-results]');
      if (button) { button.disabled = busy || failed; button.textContent = busy ? el.dataset.loading : el.dataset.viewLabel; }
    });
  };
  const refreshLinks = root => {
    const instance = root.querySelector('oa-search-filters');
    root.querySelectorAll('[data-remove-query],[data-collection-path]').forEach(link => instance?.removalURL(link));
  };
  async function navigate(url, { push = true, scroll = false, ticket = cancel() } = {}) {
    const root = document.querySelector(sectionSelector);
    if (!root || url.origin !== location.origin) { location.assign(url.href); return; }
    const searchBox = root.querySelector('oa-search');
    if (searchBox && url.searchParams.get('type') === 'product' && url.searchParams.has('q')) {
      const tags = searchBox.animalTags || new Set();
      const raw = Search.rawSearch(url, tags) || Search.rawSearch(new URL(location.href), tags);
      url.searchParams.set('q', oaScopeSearch(url.searchParams.get('q'), tags));
      Search.attachRawSearch(url, raw, tags);
    }
    const original = root.querySelector('oa-search-filters');
    if (!original) { location.assign(url.href); return; }
    status(root, original.dataset.loading, true);
    const endpoint = new URL(url);
    endpoint.searchParams.set('section_id', root.dataset.sectionId);
    request = new AbortController();
    const activeRequest = request;
    const timeout = setTimeout(() => activeRequest.abort(), 10000);
    try {
      const hit = responses.get(endpoint.href);
      let html = hit && Date.now() - hit.at < 30000 ? hit.html : null;
      if (!html) {
        const response = await fetch(endpoint, { signal: activeRequest.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Filter request failed');
        html = await response.text();
      }
      if (ticket !== sequence || !root.isConnected) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextRoot = doc.querySelector(sectionSelector);
      const content = root.querySelector('.grid__item--content');
      const nextContent = nextRoot?.querySelector('.grid__item--content');
      if (nextRoot && !nextRoot.querySelector('oa-search-filters')) { location.assign(url.href); return; }
      if (!content || !nextContent) throw new Error('Missing filter content');
      const activeId = root.contains(document.activeElement) ? document.activeElement.id : '';
      const drawerScroll = root.querySelector('.drawer__scrollable');
      const scrollTop = drawerScroll?.scrollTop || 0;
      const open = !!root.querySelector('#FilterDrawer.drawer--is-open');
      if (push && url.href !== location.href) history.pushState({ oaFilters: true }, '', url);
      content.innerHTML = nextContent.innerHTML;
      forms(root).forEach(old => {
        const fresh = nextRoot.querySelector(`[data-location="${old.dataset.location}"]`);
        if (fresh) old.sync(fresh, url);
      });
      refreshLinks(root);
      const opener = root.querySelector('.js-drawer-open-collection-filters');
      if (opener) opener.setAttribute('aria-expanded', String(open));
      if (window.theme?.FilterDrawer && open) window.theme.FilterDrawer.activeSource = opener;
      const count = nextRoot.querySelector('oa-search-filters').dataset.count;
      const freshForm = root.querySelector('oa-search-filters');
      status(root, freshForm.dataset.results.replace('[count]', count));
      root.querySelectorAll('[data-view-results]').forEach(button => { button.textContent = freshForm.dataset.viewLabel.replace('[count]', count); });
      const tabs = root.querySelector('.oa-search-page__tabs');
      const nextTabs = nextRoot.querySelector('.oa-search-page__tabs');
      if (tabs && nextTabs) tabs.innerHTML = nextTabs.innerHTML;
      const title = nextRoot.querySelector('[data-oa-document-title]')?.content.querySelector('title');
      if (title) document.title = title.textContent.trim();
      const searchBox = root.querySelector('oa-search');
      const nextSearch = nextRoot.querySelector('oa-search');
      if (searchBox && nextSearch) {
        searchBox.close(false);
        searchBox.input.value = nextSearch.querySelector('input[type="search"]').value;
        searchBox.querySelector('[name="type"]').value = nextSearch.querySelector('[name="type"]').value;
        searchBox.clear.hidden = !searchBox.input.value;
      }
      Search.restoreRawSearch(root, url);
      window.theme?.reinitProductGridItem?.(content);
      document.dispatchEvent(new CustomEvent('collection:reloaded'));
      const focused = activeId && document.getElementById(activeId);
      if (focused) focused.focus({ preventScroll: true });
      if (drawerScroll) drawerScroll.scrollTop = scrollTop;
      if (scroll && !open) content.scrollIntoView({ block: 'start', behavior: 'auto' });
      responses.delete(endpoint.href);
      responses.set(endpoint.href, { at: Date.now(), html });
      if (responses.size > 4) responses.delete(responses.keys().next().value);
    } catch (_) {
      if (ticket !== sequence) return;
      status(root, original.dataset.error, false, true);
    } finally { clearTimeout(timeout); }
  }
  class OASearchFilters extends HTMLElement {
    connectedCallback() {
      if (this.life) return;
      this.life = new AbortController();
      const on = (el, type, fn) => el?.addEventListener(type, fn, { signal: this.life.signal });
      this.form = this.querySelector('form');
      this.min = this.querySelector('[data-price-min]');
      this.max = this.querySelector('[data-price-max]');
      this.status = this.querySelector('[data-filter-status]');
      this.tagSelects = [...this.querySelectorAll('[data-oa-tag]')];
      this.initialTagHandles = this.selectedHandles();
      this.root = this.closest(sectionSelector);
      if (this.root) this.classList.add('oa-refine--instant');
      on(this, 'click', event => {
        const preset = event.target.closest('[data-budget-min]');
        if (preset && this.min && this.max) {
          const selected = preset.getAttribute('aria-pressed') === 'true';
          this.min.value = selected ? '' : preset.dataset.budgetMin;
          this.max.value = selected ? '' : preset.dataset.budgetMax;
          this.priceState();
          this.schedule(100);
        }
        if (event.target.closest('[data-view-results]')) window.theme?.FilterDrawer?.close();
      });
      on(this, 'input', event => {
        this.priceState();
        if (event.target.matches('[data-price-min],[data-price-max]')) this.schedule(650);
      });
      on(this, 'change', event => {
        if (event.target.matches('select,input[type="checkbox"]')) this.schedule(100);
      });
      on(this.form, 'submit', event => {
        this.priceState();
        if (!this.form.checkValidity()) { event.preventDefault(); this.form.reportValidity(); return; }
        this.prepare();
        if (this.root && !this.classList.contains('oa-refine--error')) {
          event.preventDefault();
          navigate(this.url());
        }
      });
      this.priceState();
      this.querySelectorAll('[data-remove-query]').forEach(link => this.removalURL(link));
      document.querySelectorAll('.oa-refine__active [data-remove-query], .oa-refine__active [data-collection-path]').forEach(link => this.removalURL(link));
    }
    sync(fresh, url) {
      Object.assign(this.dataset, fresh.dataset);
      this.form.action = fresh.querySelector('form').action;
      this.form.querySelectorAll('input[type="hidden"]').forEach(el => el.remove());
      fresh.querySelectorAll('input[type="hidden"]').forEach(el => this.form.prepend(el.cloneNode(true)));
      const groups = this.querySelector('.oa-refine__tag-groups');
      const nextGroups = fresh.querySelector('.oa-refine__tag-groups');
      if (groups && nextGroups) groups.innerHTML = nextGroups.innerHTML;
      this.tagSelects = [...this.querySelectorAll('[data-oa-tag]')];
      fresh.querySelectorAll('[data-filter-facet]').forEach(next => {
        const current = [...this.querySelectorAll('[data-filter-facet]')].find(el => el.dataset.filterFacet === next.dataset.filterFacet);
        if (!current) {
          const anchor = next.dataset.filterFacet === 'price' ? this.querySelector('.oa-refine__tag-groups') : this.querySelector('.oa-refine__actions');
          anchor?.before(next.cloneNode(true));
        } else if (next.dataset.filterFacet !== 'price') {
          const signature = el => [...el.querySelectorAll('input')].map(input => input.name + ':' + input.value).join('|');
          if (signature(current) !== signature(next)) current.innerHTML = next.innerHTML;
        }
      });
      this.min = this.querySelector('[data-price-min]');
      this.max = this.querySelector('[data-price-max]');
      ['min', 'max'].forEach(key => {
        const next = fresh.querySelector(`[data-price-${key}]`);
        if (this[key]) {
          this[key].value = url.searchParams.get(this[key].name) || '';
          if (next) this[key].placeholder = next.placeholder;
        }
      });
      this.querySelectorAll('input[type="checkbox"]').forEach(input => {
        const next = [...fresh.querySelectorAll('input[type="checkbox"]')].find(el => el.name === input.name && el.value === input.value);
        input.checked = url.searchParams.getAll(input.name).includes(input.value);
        input.disabled = !input.checked && (!next || next.disabled);
        const label = input.closest('label')?.querySelector('span');
        const nextLabel = next?.closest('label')?.querySelector('span');
        if (label && nextLabel) label.innerHTML = nextLabel.innerHTML;
        else if (label?.querySelector('small')) label.querySelector('small').textContent = '(0)';
      });
      const clear = this.querySelector('.oa-refine__actions>a');
      const nextClear = fresh.querySelector('.oa-refine__actions>a');
      if (clear && nextClear) clear.href = nextClear.href;
      this.initialTagHandles = this.selectedHandles();
      this.priceState();
    }
    prepare() {
      if (this.dataset.collectionUrl) {
        const retained = [...this.querySelectorAll('[data-current-tag]')].map(el => el.value).filter(tag => !this.initialTagHandles.includes(tag));
        const handles = [...new Set([...retained, ...this.selectedHandles()])];
        const baseURL = new URL(this.dataset.collectionUrl, location.origin);
        baseURL.pathname = baseURL.pathname.replace(/\/$/, '') + (handles.length ? '/' + handles.map(encodeURIComponent).join('+') : '');
        this.form.action = baseURL.href;
      } else {
        const tags = this.tagSelects.map(el => el.value).filter(Boolean);
        const escapeTag = value => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        const availableTags = new Set([...this.querySelectorAll('[data-oa-tag="category"] option')].map(option => option.value).filter(Boolean));
        const base = Search.rawSearch(new URL(location.href), availableTags) || this.dataset.query.trim() || '*';
        const scoped = oaScopeSearch(base, availableTags, tags.length > 0);
        this.form.elements.q.value = tags.length ? `(${scoped})${tags.map(tag => ` AND (tag:"${escapeTag(tag)}")`).join('')}` : scoped;
      }
    }
    url() {
      this.prepare();
      const url = new URL(this.form.action, location.origin);
      const params = new URLSearchParams(location.search);
      [...params.keys()].forEach(key => { if (key.startsWith('filter.') || ['q','type','page','section_id','sections','sort_by','options[prefix]'].includes(key)) params.delete(key); });
      for (const [key, value] of new FormData(this.form)) if (value !== '') params.append(key, value);
      url.search = params;
      return url;
    }
    schedule(delay) {
      if (!this.root) return;
      const ticket = cancel();
      this.priceState();
      if (!this.form.checkValidity()) {
        status(this.root, this.dataset.rangeError, false, true);
        return;
      }
      status(this.root, this.dataset.loading, true);
      timer = setTimeout(() => navigate(this.url(), { ticket }), delay);
    }
    selectedHandles() { return this.tagSelects.map(el => el.selectedOptions[0]?.dataset.tagHandle).filter(Boolean); }
    removalURL(link) {
      const url = new URL(location.href);
      if (link.dataset.collectionPath) url.pathname = link.dataset.collectionPath;
      else { url.searchParams.set('q', link.dataset.removeQuery); url.searchParams.set('type', 'product'); }
      url.searchParams.delete('page');
      link.href = url.href;
    }
    priceState() {
      if (!this.min || !this.max) return;
      const invalid = this.min.value !== '' && this.max.value !== '' && Number(this.max.value) < Number(this.min.value);
      this.max.setCustomValidity(invalid ? this.dataset.rangeError : '');
      const samePrice = (value, expected) => value === '' || expected === '' ? value === expected : Number(value) === Number(expected);
      this.querySelectorAll('[data-budget-min]').forEach(button => button.setAttribute('aria-pressed', String(samePrice(this.min.value, button.dataset.budgetMin) && samePrice(this.max.value, button.dataset.budgetMax))));
    }
    disconnectedCallback() { this.life?.abort(); this.life = null; }
  }
  customElements.define('oa-search-filters', OASearchFilters);
  document.addEventListener('change', event => {
    if (!event.target.matches('#SortBy') || !event.target.closest(sectionSelector)) return;
    event.stopImmediatePropagation();
    const root = event.target.closest(sectionSelector);
    const form = root.querySelector('oa-search-filters');
    const url = form ? form.url() : new URL(location.href);
    url.searchParams.set('sort_by', event.target.value);
    url.searchParams.delete('page');
    navigate(url);
  }, true);
  document.addEventListener('click', event => {
    const root = event.target.closest?.(sectionSelector);
    if (!root || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const productTab = event.target.closest('.oa-search-page__tabs a');
    if (productTab) {
      const url = new URL(productTab.href);
      const box = root.querySelector('oa-search');
      const raw = url.searchParams.get('q') || '';
      const query = oaScopeSearch(raw, box?.animalTags || new Set());
      if (url.origin === location.origin && url.searchParams.get('type') === 'product' && query !== raw) {
        event.preventDefault();
        url.searchParams.set('q', query);
        const original = Search.rawSearch(new URL(location.href), box?.animalTags || new Set());
        Search.attachRawSearch(url, original || raw, box?.animalTags || new Set());
        location.assign(url.href);
      }
      return;
    }
    const opener = event.target.closest('.js-drawer-open-collection-filters');
    if (opener && window.theme?.FilterDrawer) {
      event.preventDefault(); event.stopImmediatePropagation();
      document.querySelectorAll('oa-search').forEach(el => el.close?.(false));
      window.theme.FilterDrawer.open(null, opener);
      return;
    }
    const link = event.target.closest('.oa-refine__active a,.oa-refine__actions>a,.pagination a,.oa-refine__empty .btn,.oa-search-page__empty .btn');
    if (!link || link.hasAttribute('download') || link.target === '_blank') return;
    const url = new URL(link.href);
    if (url.origin !== location.origin) return;
    event.preventDefault(); event.stopImmediatePropagation();
    navigate(url, { scroll: !!link.closest('.pagination') });
  }, true);
  window.addEventListener('popstate', () => {
    if (document.querySelector(sectionSelector)) navigate(new URL(location.href), { push: false });
  });
})();

